import { create } from "zustand";
import api from "../../utils/api";
import { getError } from "../../utils/getError";

// Types
export interface NetworkConfig {
  ip_address: string | null;
  port: string | null;
  protocol: string | null;
}

export interface Brand {
  id: string;
  brand: string;
  model: string;
  network: NetworkConfig | null;
  operational_alias: string | null;
  origin: "v2" | "v4" | "v5" | null;
  collections?: string[];
  createdAt?: { _seconds: number };
  updatedAt?: { _seconds: number };
}

export interface CommandTransport {
  channel: string;
  mode: string;
  value: string;
}

export interface Command {
  id: string;
  code: string;
  name: string;
  data: boolean;
  enabled: boolean;
  password_required: boolean;
  response_expected: boolean;
  roles: string[];
  transport: CommandTransport;
  sms_body: string | null;
  sms_enabled: boolean;
  createdAt?: { _seconds: number };
  updatedAt?: { _seconds: number };
  company_id?: string | null;
}

export interface BrandFormData {
  brand: string;
  model: string;
  network?: NetworkConfig | null;
  operational_alias?: string | null;
  origin?: "v2" | "v4" | "v5" | null;
}

export interface CommandFormData {
  code: string;
  name: string;
  data: boolean;
  password_required: boolean;
  roles: string[];
  transport: {
    value: string;
  };
  sms_enabled: boolean;
  sms_body: string | null;
}

interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface FilterParams {
  page?: number;
  limit?: number;
  search?: string;
  brand?: string;
  model?: string;
  protocol?: string;
}

interface CommandFilterParams {
  page?: number;
  limit?: number;
  search?: string;
  gprs_activated?: string;
  password_required?: string;
}

interface BrandsState {
  // Brands
  brands: Brand[];
  allBrands: Brand[];
  allBrandsLoading: boolean;
  selectedBrand: Brand | null;
  brandsLoading: boolean;
  brandsMeta: PaginationMeta | null;

  // Count (para el badge del sidebar, no afecta la lista)
  brandsCount: number;
  brandsCountLoading: boolean;

  // Commands
  commands: Command[];
  selectedCommand: Command | null;
  commandsLoading: boolean;
  commandsMeta: PaginationMeta | null;

  // Actions - Brands
  fetchBrands: (params?: FilterParams) => Promise<void>;
  fetchBrandsCount: () => Promise<void>;
  fetchAllBrands: () => Promise<void>;
  fetchBrand: (id: string) => Promise<Brand | null>;
  createBrand: (data: BrandFormData) => Promise<{ success: boolean; error?: string; data?: Brand }>;
  updateBrand: (id: string, data: Partial<BrandFormData>) => Promise<{ success: boolean; error?: string }>;
  deleteBrand: (id: string) => Promise<{ success: boolean; error?: string }>;
  setSelectedBrand: (brand: Brand | null) => void;

  // Actions - Commands
  fetchCommands: (brandId: string, params?: CommandFilterParams) => Promise<void>;
  fetchCommand: (brandId: string, commandId: string) => Promise<Command | null>;
  createCommand: (brandId: string, data: CommandFormData) => Promise<{ success: boolean; error?: string; data?: Command }>;
  updateCommand: (brandId: string, commandId: string, data: Partial<CommandFormData>) => Promise<{ success: boolean; error?: string }>;
  deleteCommand: (brandId: string, commandId: string) => Promise<{ success: boolean; error?: string }>;
  setSelectedCommand: (command: Command | null) => void;
  clearCommands: () => void;
}

export const useBrandsStore = create<BrandsState>((set, get) => ({
  // Initial state
  brands: [],
  allBrands: [],
  allBrandsLoading: false,
  selectedBrand: null,
  brandsLoading: false,
  brandsMeta: null,
  brandsCount: 0,
  brandsCountLoading: false,

  commands: [],
  selectedCommand: null,
  commandsLoading: false,
  commandsMeta: null,

  // ==================
  // BRANDS ACTIONS
  // ==================

  fetchAllBrands: async () => {
    set({ allBrandsLoading: true });
    try {
      const response = await api.get("/brandsList?limit=500");
      set({ allBrands: response.data.data });
    } catch (error) {
      console.error("Error fetching all brands:", error);
    }
    finally {
      set({ allBrandsLoading: false });
    }
  },

  fetchBrands: async (params?: FilterParams) => {
    set({ brandsLoading: true });
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append("page", String(params.page));
      if (params?.limit) queryParams.append("limit", String(params.limit));
      if (params?.search) queryParams.append("search", params.search);
      if (params?.brand) queryParams.append("brand", params.brand);
      if (params?.model) queryParams.append("model", params.model);
      if (params?.protocol) queryParams.append("protocol", params.protocol);

      // Endpoint dedicado: filtra los modelos por los dispositivos de la
      // compañía seleccionada (company_id lo inyecta el interceptor de api).
      // Si no hay compañía seleccionada, devuelve todos los modelos.
      const response = await api.get(`/brandsByCompany?${queryParams.toString()}`);

      if (response.data?.data) {
        const meta = response.data.meta;
        set({
          brands: response.data.data,
          brandsMeta: {
            page: meta?.currentPage || 1,
            limit: params?.limit || 20,
            total: meta?.totalItems || 0,
            totalPages: meta?.totalPages || 1,
          },
        });
      }
    } catch (error) {
      console.error("Error fetching brands:", error);
      set({ brands: [] });
    } finally {
      set({ brandsLoading: false });
    }
  },

  fetchBrandsCount: async () => {
    set({ brandsCountLoading: true });
    try {
      // limit=1: el total real viene en meta.totalItems sin traer toda la lista.
      // company_id lo inyecta el interceptor según la compañía seleccionada.
      const response = await api.get(`/brandsByCompany?limit=1&page=1`);
      const total = response.data?.meta?.totalItems ?? 0;
      set({ brandsCount: total });
    } catch (error) {
      console.error("Error fetching brands count:", error);
    } finally {
      set({ brandsCountLoading: false });
    }
  },

  fetchBrand: async (id: string) => {
    try {
      const response = await api.get(`/brands?id=${id}`);
      if (response.data?.data) {
        return response.data.data;
      }
      return null;
    } catch (error) {
      console.error("Error fetching brand:", error);
      return null;
    }
  },

  createBrand: async (data: BrandFormData) => {
    try {
      const response = await api.post("/brands", data);
      if (response.data?.data) {
        // Refresh list
        get().fetchBrands();
        return { success: true, data: response.data.data };
      }
      return { success: false, error: "Error creating brand" };
    } catch (error: any) {
      console.error("Error creating brand:", error);
      return {
        success: false,
        error: getError(error)
      };
    }
  },

  updateBrand: async (id: string, data: Partial<BrandFormData>) => {
    try {
      const response = await api.put(`/brands?id=${id}`, data);
      if (response.data) {
        // Refresh list
        get().fetchBrands();
        return { success: true };
      }
      return { success: false, error: "Error updating brand" };
    } catch (error: any) {
      console.error("Error updating brand:", error);
      return {
        success: false,
        error: getError(error)
      };
    }
  },

  deleteBrand: async (id: string) => {
    try {
      await api.delete(`/brands?id=${id}`);
      // Refresh list
      get().fetchBrands();
      return { success: true };
    } catch (error: any) {
      console.error("Error deleting brand:", error);
      return {
        success: false,
        error: getError(error)
      };
    }
  },

  setSelectedBrand: (brand: Brand | null) => {
    set({ selectedBrand: brand });
  },

  // ==================
  // COMMANDS ACTIONS
  // ==================

  fetchCommands: async (brandId: string, params?: CommandFilterParams) => {
    set({ commandsLoading: true });
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("brand_id", brandId);
      if (params?.page) queryParams.append("page", String(params.page));
      if (params?.limit) queryParams.append("limit", String(params.limit));
      if (params?.search) queryParams.append("search", params.search);
      if (params?.gprs_activated) queryParams.append("gprs_activated", params.gprs_activated);
      if (params?.password_required) queryParams.append("password_required", params.password_required);

      const response = await api.get(`/commandsList?${queryParams.toString()}`);

      if (response.data?.data) {
        const meta = response.data.meta;
        set({
          commands: response.data.data,
          commandsMeta: {
            page: meta?.currentPage || 1,
            limit: params?.limit || 20,
            total: meta?.totalItems || 0,
            totalPages: meta?.totalPages || 1,
          },
        });
      }
    } catch (error) {
      console.error("Error fetching commands:", error);
      set({ commands: [] });
    } finally {
      set({ commandsLoading: false });
    }
  },

  fetchCommand: async (brandId: string, commandId: string) => {
    try {
      const response = await api.get(`/commands?brand_id=${brandId}&id=${commandId}`);
      if (response.data?.data) {
        return response.data.data;
      }
      return null;
    } catch (error) {
      console.error("Error fetching command:", error);
      return null;
    }
  },

  createCommand: async (brandId: string, data: CommandFormData) => {
    try {
      const response = await api.post(`/commands?brand_id=${brandId}`, data);
      if (response.data?.data) {
        // Refresh list
        get().fetchCommands(brandId);
        return { success: true, data: response.data.data };
      }
      return { success: false, error: "Error creating command" };
    } catch (error: any) {
      console.error("Error creating command:", error);
      return {
        success: false,
        error: getError(error)
      };
    }
  },

  updateCommand: async (brandId: string, commandId: string, data: Partial<CommandFormData>) => {
    try {
      const response = await api.put(`/commands?brand_id=${brandId}&id=${commandId}`, data);
      if (response.data) {
        // Refresh list
        get().fetchCommands(brandId);
        return { success: true };
      }
      return { success: false, error: "Error updating command" };
    } catch (error: any) {
      console.error("Error updating command:", error);
      return {
        success: false,
        error: getError(error)
      };
    }
  },

  deleteCommand: async (brandId: string, commandId: string) => {
    try {
      await api.delete(`/commands?brand_id=${brandId}&id=${commandId}`);
      // Refresh list
      get().fetchCommands(brandId);
      return { success: true };
    } catch (error: any) {
      console.error("Error deleting command:", error);
      return {
        success: false,
        error: getError(error)
      };
    }
  },

  setSelectedCommand: (command: Command | null) => {
    set({ selectedCommand: command });
  },

  clearCommands: () => {
    set({ commands: [], commandsMeta: null, selectedCommand: null });
  },
}));
